import { observable, action, computed } from 'mobx';

import {
  AppManager,
  Variables,
  ConnectionAppManager,
  Input,
  routerStore,
} from 'core';

import { StoryApp, StoryModel, StoryArrayApp } from 'apps/story/app';
import { StoryQuery, StoryMutation, ProfileStoriesQuery } from 'apps/story/gql';

import i18next from 'i18next';

export class StoryAppManager extends AppManager {
  static registryName = 'StoryAppManager';
  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('story', StoryApp);

    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
    });
    this.variables.onChange(['space', 'slug'], this.StoryQuery);
  }

  disposeWhen() {
    return !routerStore.route.name.includes('story');
  }

  @computed get loading() {
    return this.queries.get('story')?.loading;
  }

  @computed get notFound() {
    return !this.loading && !this.story;
  }

  @action StoryQuery = () => {
    const q = new StoryQuery(this, {
      queryKey: 'story',
      onSysErr: this.onStoryError,
    });
    q.process(story => StoryModel.Load(story), this.StoryUpdate);
    q.query();
  };

  @action onStoryError = error => {
    console.error(error);
  };

  @action StoryUpdate = storyModel => {
    if (!storyModel) return;
    this.story = storyModel;
    this.story.createForms();
  };

  @action Save = story => {
    const input = new Input({
      id: () => story.id,
      title: () => story.title,
      body: () => story.body,
      spaceId: () => story.inSpace.id,
      tags: () =>
        story.tags.map(({ id, name, deleted }) => ({ id, name, deleted })),
      avatar: () => (story.avatar ? this.story.avatar.id : null),
    });
    const mutation = new StoryMutation(input);
    mutation.process(
      storyModel => StoryModel.Load(storyModel),
      this.StoryUpdate,
    );
    const options = {
      onSysErr: story.onSaveStoryErr,
      onUserErr: story.onSaveStoryErr,
      onSuccess: story.onSaveStorySuccess,
    };
    mutation.setCallbacks(options);
    mutation.mutate();
    return mutation;
  };

  @action Delete = story => {
    const input = new Input({
      id: () => story.id,
      deleted: () => true,
    });
    const options = {
      onSysErr: story.onDeleteErr,
      onUserErr: story.onDeleteErr,
      onSuccess: story.onDeleteSuccess,
    };
    const mutation = new StoryMutation(input);
    mutation.setCallbacks(options);
    mutation.mutate();
    return mutation;
  };
}

export class StoryConnManager extends ConnectionAppManager {
  static ArrayApp = StoryArrayApp;
  constructor(parent, init, options) {
    super(parent, init, options);
    this.initialBatchSize =
      init.initialBatchSize || this.arrayApp.cardLayout.initialBatchSize;
    this.batchSize = this.arrayApp.cardLayout.batchSize;
    this.contain = init.contains || [];
    this.whitelabels = init.whitelabels || [];
    this.variables = new Variables({
      sortBy: () => this.sortBy,
      featuredStorysSortBy: () => this.sortBy,
      batchSize: () =>
        this.loadedMore ? this.batchSize : this.initialBatchSize,
      featuredStorysCount: () =>
        this.loadedMore ? this.batchSize : this.initialBatchSize,
      cursor: () => this.endCursor,
      filterBy: () => this.filterBy,
      contains: () => this.contains,
      whitelabels: () => this.whitelabels,
    });
    this.loadMoreQuery = init.loadMoreQuery || ProfileStoriesQuery;
    this.filterBy = init.filterBy || [];
    this.sortOptions = init.sortOptions || [
      ['recent', i18next.t('Recent')],
      ['-likes', i18next.t('Likes')],
      ['pageviews', i18next.t('Views')],
    ];
  }

  @observable sortBy = 'recent';
  @observable sortOptions = [
    ['recent', i18next.t('Recent')],
    ['-likes', i18next.t('Likes')],
  ];

  @action NewUsername() {
    this.lastLoadedCount = 0;
    this.Reset();
  }

  @action SetSort(sort) {
    this.sortBy = sort;
    this.Clear();
    this.loadMore();
  }
}
